export const MIN_CONTENT_CUT_PX = 12;
export const MAX_CENTRE_DRIFT_PX = 24;

const PADDING_ADJUSTMENTS_PX = Array.from({ length: 29 }, (_, step) => -6 + step / 2).sort(
    (a, b) => Math.abs(a) - Math.abs(b) || a - b,
);

type Span = { start: number; end: number };

function spansOf(widths: readonly number[]): Span[] {
    let cursor = 0;
    return widths.map((width) => {
        const span = { start: cursor, end: cursor + width };
        cursor = span.end;
        return span;
    });
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function contentCut(spans: readonly Span[], edge: number, tabPadding: number): number {
    const cutTab = spans.find((span) => span.start <= edge && edge < span.end);
    return cutTab ? Math.min(edge - cutTab.start, cutTab.end - edge) - tabPadding : -tabPadding;
}

function peekScore(spans: readonly Span[], viewport: number, tabPadding: number, scrollLeft: number): number {
    const contentWidth = spans.at(-1)?.end ?? 0;
    const cuts: number[] = [];
    if (scrollLeft > 0.5) {
        cuts.push(contentCut(spans, scrollLeft, tabPadding));
    }
    if (scrollLeft + viewport < contentWidth - 0.5) {
        cuts.push(contentCut(spans, scrollLeft + viewport, tabPadding));
    }
    return Math.min(MIN_CONTENT_CUT_PX, ...cuts);
}

export function restingScrollLeft(
    widths: readonly number[],
    viewport: number,
    tabPadding: number,
    selectedIndex: number,
): number {
    const spans = spansOf(widths);
    const selected = spans[selectedIndex];
    const maxScrollLeft = Math.max(0, (spans.at(-1)?.end ?? 0) - viewport);
    if (!selected) {
        return 0;
    }
    const centred = clamp((selected.start + selected.end - viewport) / 2, 0, maxScrollLeft);
    if (centred === 0 || centred === maxScrollLeft) {
        return centred;
    }

    let best = { scrollLeft: centred, score: peekScore(spans, viewport, tabPadding, centred) };
    for (let drift = 1; drift <= MAX_CENTRE_DRIFT_PX && best.score < MIN_CONTENT_CUT_PX; drift += 1) {
        for (const candidate of [centred - drift, centred + drift]) {
            const scrollLeft = clamp(candidate, 0, maxScrollLeft);
            const keepsSelectedInView = selected.start >= scrollLeft && selected.end <= scrollLeft + viewport;
            const score = peekScore(spans, viewport, tabPadding, scrollLeft);
            if (keepsSelectedInView && score > best.score) {
                best = { scrollLeft, score };
            }
        }
    }
    return best.scrollLeft;
}

export function tabPaddingAdjustment(baseWidths: readonly number[], viewport: number, basePadding: number): number {
    if (baseWidths.reduce((total, width) => total + width, 0) <= viewport) {
        return 0;
    }
    let best: { adjustment: number; opening: number; worst: number } | undefined;
    for (const adjustment of PADDING_ADJUSTMENTS_PX) {
        const widths = baseWidths.map((width) => width + 2 * adjustment);
        const spans = spansOf(widths);
        if ((spans.at(-1)?.end ?? 0) <= viewport) {
            continue;
        }
        const tabPadding = basePadding + adjustment;
        const opening = peekScore(spans, viewport, tabPadding, 0);
        const worst = Math.min(
            ...widths.map((_, index) =>
                peekScore(spans, viewport, tabPadding, restingScrollLeft(widths, viewport, tabPadding, index)),
            ),
        );
        if (!best || opening > best.opening || (opening === best.opening && worst > best.worst)) {
            best = { adjustment, opening, worst };
        }
    }
    return best?.adjustment ?? 0;
}
